import { makeStyles } from '@material-ui/styles';
import { resolveColor } from '@marqvision/mds-v2';
import { useWrapperSize } from '@marqvision/shared';
import { ChartProps } from './@types';
import { copyLastValue, getCoordinates, getPathData, getSum } from './@utils';

const useStyles = makeStyles(() => ({
  path: {
    transition: '0.3s',
  },
  svgContainer: {
    overflow: 'visible',
  },
}));

export const FunnelChart = (props: ChartProps) => {
  const { total, referenceLine, data, lineColor = 'color/border/neutral/weak/normal' } = props;
  const classes = useStyles();

  const { wrapperRef, width, height } = useWrapperSize();

  const dataList = (Array.isArray(data) ? data : [data]).map((item) => ({
    ...item,
    data: copyLastValue(item.data), // 마지막 섹션 일자로 만들기 위해서 마지막 데이터 복사
  }));
  const sumData = getSum(dataList);
  const totalData = total ? copyLastValue(total.data) : sumData;
  const remaining = total // total 값이 있으면 total 값에서 나머지 전체값을 뺀 잔여값을 최상단 출력
    ? {
        color: total.color,
        data: totalData.map((totalValue, index) => totalValue - sumData[index]),
      }
    : undefined;
  const chartData = remaining ? [remaining, ...dataList] : dataList;

  const coordinates = getCoordinates(totalData, chartData, width, height);
  const referenceLineTop = `${referenceLine?.top}px` || '0';
  const referenceLineBottom = `${referenceLine?.bottom}px` || '0';

  return (
    <div style={{ paddingTop: referenceLineTop, paddingBottom: referenceLineBottom }}>
      <div
        style={{ height: props.height || `calc(100% - ${referenceLineTop} - ${referenceLineBottom})` }}
        ref={wrapperRef}
      >
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${width} ${height}`}
          xmlns="http://www.w3.org/2000/svg"
          className={classes.svgContainer}
        >
          <defs>
            {chartData.map(({ color }, dataIndex) => {
              const colors = Array.isArray(color) ? color : [color];
              return (
                <linearGradient
                  key={`funnel-gradient-${dataIndex}`}
                  id={`funnel-gradient-${dataIndex}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2={height}
                  gradientUnits="userSpaceOnUse"
                >
                  {colors.map((color, colorIndex) => {
                    return (
                      <stop
                        key={`funnel-gradient-stop-${dataIndex}-${colorIndex}`}
                        offset={colorIndex / (colors.length - 1)}
                        stopColor={color}
                      />
                    );
                  })}
                </linearGradient>
              );
            })}
          </defs>
          {chartData.map((_, index) => (
            <path
              key={`funnel-path-${index}`}
              className={classes.path}
              d={getPathData(coordinates, index)}
              fill={`url(#funnel-gradient-${index})`}
            />
          ))}
          {coordinates.map(
            ({ x }, index) =>
              !!index &&
              index !== totalData.length - 1 && (
                <line
                  key={`funnel-line-${index}`}
                  className={classes.path}
                  x1={x}
                  x2={x}
                  y1={`-${referenceLineTop}`}
                  y2={`${height + (referenceLine?.bottom || 0)}`}
                  stroke={resolveColor(lineColor)}
                  strokeWidth="1"
                />
              )
          )}
        </svg>
      </div>
    </div>
  );
};


// 사용 예시
// <FunnelChart
//   height="200px"
//   referenceLine={{ top: 100 }}
//   total={{
//     color: [
//       MDSThemeValue._raw_color.teal500,
//       MDSThemeValue._raw_color.teal400,
//       MDSThemeValue._raw_color.teal300,
//       MDSThemeValue._raw_color.teal200,
//       MDSThemeValue._raw_color.teal100,
//     ],
//     data: [10000, 7000, 5000, 3000],
//   }}
//   data={{
//     color: [
//       MDSThemeValue._raw_color.blue900,
//       MDSThemeValue._raw_color.blue700,
//       MDSThemeValue._raw_color.blue600,
//       MDSThemeValue._raw_color.blue300,
//     ],
//     data: [10000, 4000, 1500, 500],
//   }}
// />